import endpoints from 'constants/endpoints';

export const agencyRecommendationsKeys = {
  all: ['agency-recommendations'],
  // one prefix per widget: a suggest only invalidates the list it came from, so a
  // failing query never gets invalidated into refetching itself
  notAppointedPoliciesAll: () => [
    ...agencyRecommendationsKeys.all,
    'not-appointed-policies'
  ],
  notAppointedPolicies: agencyId => [
    ...agencyRecommendationsKeys.notAppointedPoliciesAll(),
    String(agencyId)
  ],
  stateLeadersAll: () => [
    ...agencyRecommendationsKeys.all,
    'state-leader-missing-appointments'
  ],
  stateLeaders: agencyId => [
    ...agencyRecommendationsKeys.stateLeadersAll(),
    String(agencyId)
  ],
  carrierMissingAll: () => [
    ...agencyRecommendationsKeys.all,
    'carrier-missing-appointments'
  ],
  carrierMissing: (agencyId, carrierId) => [
    ...agencyRecommendationsKeys.carrierMissingAll(),
    String(agencyId),
    String(carrierId)
  ]
};

// the three recommendation endpoints only exist in the O'Neill block of endpoints.js.
// for Sunshine the routes are undefined, so the widgets have to stay empty instead of
// firing requests at a path that isn't there — apiRequest would throw and the section
// would surface a load error for a feature that company simply does not have.
export const hasNotAppointedPoliciesEndpoint = () =>
  Boolean(endpoints?.aws?.notAppointedPolicies);
export const hasStateLeaderEndpoint = () =>
  Boolean(endpoints?.aws?.stateLeaderMissingAppointments);
export const hasCarrierMissingEndpoint = () =>
  Boolean(endpoints?.aws?.carrierMissingAppointments);

/**
 * policies sold without an active appointment — feeds the "Commission at risk" cards.
 * @param agencyId - zoho agency id
 */
export function notAppointedPoliciesRequest(agencyId) {
  return {
    method: 'POST',
    path: endpoints.aws.notAppointedPolicies,
    data: { agencyId }
  };
}

/**
 * top state leaders and the carriers they are not appointed with — feeds the
 * "Coverage gap" cards.
 * @param agencyId - zoho agency id
 */
export function stateLeaderMissingAppointmentsRequest(agencyId) {
  return {
    method: 'POST',
    path: endpoints.aws.stateLeaderMissingAppointments,
    data: { agencyId }
  };
}

/**
 * agents selling heavily with the featured carrier but not appointed with it.
 * @param agencyId - zoho agency id
 * @param carrierId - id of the featured carrier
 */
export function carrierMissingAppointmentsRequest(agencyId, carrierId) {
  return {
    method: 'POST',
    path: endpoints.aws.carrierMissingAppointments,
    data: { agencyId, carrierId }
  };
}

// the transport still wraps the payload in some deployments; peel the envelope
// before reading the contract. only the envelope — never a contract key.
function unwrapEnvelope(payload) {
  if (payload == null) return null;
  if (typeof payload === 'string') {
    try {
      return unwrapEnvelope(JSON.parse(payload));
    } catch {
      return null;
    }
  }
  if (typeof payload !== 'object' || Array.isArray(payload)) return payload;

  if (payload.body != null) return unwrapEnvelope(payload.body);
  if (payload.details?.output != null) {
    return unwrapEnvelope(payload.details.output);
  }
  return payload;
}

export function extractArray(payload) {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload === 'string') {
    try {
      return extractArray(JSON.parse(payload));
    } catch {
      return [];
    }
  }
  if (typeof payload !== 'object') return [];

  const wrapperKeys = [
    'data',
    'results',
    'items',
    'rows',
    'records',
    'policies',
    'gaps',
    'recommendations',
    'list'
  ];
  for (const key of wrapperKeys) {
    if (Array.isArray(payload[key])) return payload[key];
  }

  if (typeof payload.body === 'string') {
    try {
      return extractArray(JSON.parse(payload.body));
    } catch {
      return [];
    }
  }
  if (payload.body && typeof payload.body === 'object') {
    return extractArray(payload.body);
  }

  const output = payload?.details?.output;
  if (typeof output === 'string') {
    try {
      return extractArray(JSON.parse(output));
    } catch {
      return [];
    }
  }
  if (Array.isArray(output)) return output;

  const ownKeys = Object.keys(payload);
  if (ownKeys.length > 0) return [payload];

  return [];
}

export function pickArray(obj, keys) {
  if (!obj || typeof obj !== 'object') return [];
  for (const k of keys) {
    if (Array.isArray(obj[k])) return obj[k];
  }
  return [];
}

export function pickValue(obj, keys) {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return undefined;
}

/**
 * reads the leaders out of a state-leader-missing-appointments response. the
 * endpoint now answers `{ leaders, partial, skippedAgents }` with up to 3 leaders;
 * the single-leader-at-the-root shape is still accepted so the widget survives the
 * deploy window and a backend rollback.
 * @param payload - response body
 */
export function readStateLeaders(payload) {
  const body = unwrapEnvelope(payload);
  if (Array.isArray(body?.leaders)) return body.leaders;
  // pre-`leaders` contract: one leader at the root, or null when there was none
  if (body?.agentId || body?.agent_id) return [body];
  return [];
}

/**
 * reads the `partial` / `skippedAgents` meta both recommendation endpoints now
 * return. `partial` means the agency was evaluated fine but N agents could not be
 * read from Zoho, so they are missing from an otherwise valid list.
 * @param payload - response body
 */
export function readPartialMeta(payload) {
  const body = unwrapEnvelope(payload);
  const skipped = Number(body?.skippedAgents);
  return {
    partial: body?.partial === true,
    skippedAgents: Number.isFinite(skipped) && skipped > 0 ? skipped : 0
  };
}

/**
 * merges the `partial` meta of several agencies into one notice: OR over `partial`,
 * sum over `skippedAgents`.
 * @param metas - list of { partial, skippedAgents }
 */
export function mergePartialMeta(metas) {
  let partial = false;
  let skippedAgents = 0;
  for (const meta of metas ?? []) {
    if (!meta) continue;
    if (meta.partial) partial = true;
    skippedAgents += meta.skippedAgents || 0;
  }
  return { partial, skippedAgents };
}
